import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { PageIndices, TabIndices } from '../common/i-doc/i-doc.registry.ts';
import { DashboardLayout } from '@components/DashboardLayout.tsx';
import { PageCard } from '@components/common/PageCard.tsx';
import { PageHint } from '@components/common/PageHint.tsx';
import { AdaptiveTabs, AdaptiveTabItem, useAdaptiveTab } from '@components/common/AdaptiveTabs.tsx';
import { FinanceLegacyEntriesTab } from '@components/finance-legacy/FinanceLegacyEntriesTab.tsx';
import { FinanceLegacyEntriesByTypeTab } from '@components/finance-legacy/FinanceLegacyEntriesByTypeTab.tsx';
import { FinanceLegacyStatsTab } from '@components/finance-legacy/FinanceLegacyStatsTab.tsx';
import { FinanceLegacyStats2Tab } from '@components/finance-legacy/FinanceLegacyStats2Tab.tsx';
import { FinanceLegacyProjectsTab } from '@components/finance-legacy/FinanceLegacyProjectsTab.tsx';
import { FinanceLegacyEmployeesTab } from '@components/finance-legacy/FinanceLegacyEmployeesTab.tsx';
import { FinanceLegacySourcesTab } from '@components/finance-legacy/FinanceLegacySourcesTab.tsx';
import { FinanceLegacyCurrenciesTab } from '@components/finance-legacy/FinanceLegacyCurrenciesTab.tsx';
import { FinanceLegacyUsersTab } from '@components/finance-legacy/FinanceLegacyUsersTab.tsx';
import { FinanceLegacyProfitTab } from '@components/finance-legacy/FinanceLegacyProfitTab.tsx';
import { financeLegacyService, FinanceLegacyOptions, ENTRY_TYPE_EXPENSE, ENTRY_TYPE_INCOME } from '@services/finance-legacy.service.ts';
import { toastService } from '@services/toast.service.ts';

// Легасі-бухгалтерія живого, непов'язаного проєкту (elchukof_fin) - окрема
// БД. Здебільшого read-only, окрім власних (sourceApp=web) записів у
// "Витрати"/"Доходи" (2026-08-21). Не плутати з "Фінанси" (sidebar.group.finance) -
// той розділ про клієнтів/експертів ЦЬОГО застосунку.
// workspace/current-work/finance-legacy-integration.md

// "Профіт" і "Статистика" свідомо в кінці (за проханням користувача) - решта
// табів це основні дані/довідники, ці два - агрегати/окремі журнали, не
// первинні дані. "Витрати"/"Доходи" - експеримент (2026-08-21), спереду:
// та сама таблиця "Записи", розділена по type. Сам таб "Записи" не чіпали.
const TABS: AdaptiveTabItem[] = [
  { value: 'expenses', label: 'Витрати', iDoc: TabIndices.DEV_FINANCE_LEGACY_EXPENSES },
  { value: 'income', label: 'Доходи', iDoc: TabIndices.DEV_FINANCE_LEGACY_INCOME },
  { value: 'entries', label: 'Записи', iDoc: TabIndices.DEV_FINANCE_LEGACY_ENTRIES },
  { value: 'projects', label: 'Проєкти', iDoc: TabIndices.DEV_FINANCE_LEGACY_PROJECTS },
  { value: 'employees', label: 'Співробітники', iDoc: TabIndices.DEV_FINANCE_LEGACY_EMPLOYEES },
  { value: 'sources', label: 'Джерела', iDoc: TabIndices.DEV_FINANCE_LEGACY_SOURCES },
  { value: 'currencies', label: 'Валюти', iDoc: TabIndices.DEV_FINANCE_LEGACY_CURRENCIES },
  { value: 'users', label: 'Юзери', iDoc: TabIndices.DEV_FINANCE_LEGACY_USERS },
  { value: 'stats', label: 'Статистика', iDoc: TabIndices.DEV_FINANCE_LEGACY_STATS },
  { value: 'stats2', label: 'Статистика 2', iDoc: TabIndices.DEV_FINANCE_LEGACY_STATS2 },
  { value: 'profit', label: 'Профіт', iDoc: TabIndices.DEV_FINANCE_LEGACY_PROFIT },
];

const emptyOptions: FinanceLegacyOptions = { projects: [], sources: [], employees: [], currencies: [] };

const DevFinanceLegacyPageContent: React.FC = () => {
  const [options, setOptions] = useState<FinanceLegacyOptions>(emptyOptions);

  useEffect(() => {
    financeLegacyService.getOptions()
      .then(setOptions)
      .catch(() => toastService.error('Не вдалося завантажити довідники для фільтрів.'));
  }, []);

  const tab = useAdaptiveTab(TABS, { searchParamKey: 'tab', defaultValue: 'entries', storageKey: 'tab:dev-finance-legacy' });

  return (
    <Box data-i-doc={PageIndices.DEV_FINANCE_LEGACY}>
      <PageHint iDoc={PageIndices.DEV_FINANCE_LEGACY} />

      <PageCard>
        <AdaptiveTabs tabs={TABS} searchParamKey="tab" defaultValue="entries" storageKey="tab:dev-finance-legacy" />

        {tab === 'expenses' && <FinanceLegacyEntriesByTypeTab options={options} type={ENTRY_TYPE_EXPENSE} iDoc={TabIndices.DEV_FINANCE_LEGACY_EXPENSES} />}
        {tab === 'income' && <FinanceLegacyEntriesByTypeTab options={options} type={ENTRY_TYPE_INCOME} iDoc={TabIndices.DEV_FINANCE_LEGACY_INCOME} />}
        {tab === 'stats' && <FinanceLegacyStatsTab options={options} />}
        {tab === 'stats2' && <FinanceLegacyStats2Tab />}
        {tab === 'projects' && <FinanceLegacyProjectsTab options={options} />}
        {tab === 'employees' && <FinanceLegacyEmployeesTab options={options} />}
        {tab === 'sources' && <FinanceLegacySourcesTab options={options} />}
        {tab === 'currencies' && <FinanceLegacyCurrenciesTab />}
        {tab === 'users' && <FinanceLegacyUsersTab />}
        {tab === 'profit' && <FinanceLegacyProfitTab options={options} />}
        {(!tab || tab === 'entries') && <FinanceLegacyEntriesTab options={options} />}
      </PageCard>
    </Box>
  );
};

export const DevFinanceLegacyPage: React.FC = () => (
  <DashboardLayout>
    <DevFinanceLegacyPageContent />
  </DashboardLayout>
);
